// ============================================================
// SideQuests - In-App Notification Center (Track D)
//
// Real-time listener on notifications/{id} (own uid, server-written).
// Unread badge + "Mark all read". Deep links navigate to the quest,
// the chat thread, or the profile for the referenced item.
//
// Integration with the FCM → toast bridge stays untouched:
// subscribeNotifications only reflects the Firestore collection, and
// subscribeToForegroundMessages handles the push path separately.
// No duplicate notifications: this reads server-written docs only - 
// foreground FCM toasts are ephemeral UI, never persisted here.
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import {
  AppNotification,
  subscribeNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  unreadCount,
} from '../services/notifications';
import { useAuth } from '../context/AuthContext';
import { CommunityFeedSkeleton } from './Skeleton';
import Glyph, { glyphForNotificationType } from './Glyphs';

interface NotificationCenterProps {
  onNavigate: (page: string) => void;
}

function timeAgo(d: Date): string {
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString();
}

function openTarget(n: AppNotification, onNavigate: (p: string) => void) {
  if (n.jobId) {
    // Quest updates / payments / boosts → quest detail.
    onNavigate(`quest/${n.jobId}`);
  } else if (n.type === 'chat') {
    onNavigate('inbox');
  } else if (n.type === 'level_up' || n.type === 'xp') {
    onNavigate('profile');
  }
}

export default function NotificationCenter({ onNavigate }: NotificationCenterProps) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const unsub = subscribeNotifications(user.uid, (items) => {
      setNotifications(items);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [user]);

  const handleClick = useCallback(async (n: AppNotification) => {
    if (!n.read) {
      try { await markNotificationRead(n.id); } catch { /* best-effort */ }
    }
    openTarget(n, onNavigate);
  }, [onNavigate]);

  const handleMarkAll = useCallback(async () => {
    if (!user || notifications.length === 0) return;
    try { await markAllNotificationsRead(user.uid); } catch { /* best-effort */ }
  }, [user, notifications]);

  const unread = unreadCount(notifications);

  return (
    <div className="section" style={{ paddingBottom: 100 }}>
      <div className="section-header" style={{ marginBottom: 4 }}>
        <h2 className="section-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Glyph name="bell" size={16} /> Notifications
        </h2>
        {unread > 0 && (
          <button className="section-link" onClick={handleMarkAll}>Mark all read</button>
        )}
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 12px' }}>
        {unread > 0 ? `${unread} unread` : 'You’re all caught up'}
      </p>

      {loading ? (
        <CommunityFeedSkeleton count={5} />
      ) : notifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Glyph name="bell-off" size={22} /></div>
          <div className="empty-state-title">No notifications yet</div>
          <div className="empty-state-text">Quest updates, chat messages, boosts, approvals and payouts will land here.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notifications.map((n) => {
            const unreadItem = !n.read;
            return (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className="js-card-lift"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12, textAlign: 'left', width: '100%',
                  background: unreadItem ? 'var(--badge-info-bg)' : 'var(--card-bg)',
                  border: '1px solid ' + (unreadItem ? 'var(--badge-info-border)' : 'var(--border-color)'),
                  borderRadius: 12, padding: 12, cursor: 'pointer', fontFamily: 'inherit',
                  color: 'var(--text-primary)', minHeight: 48,
                }}
                aria-label={`${n.title}. ${n.body}. ${unreadItem ? 'Unread' : 'Read'}`}
              >
                <span style={{ flexShrink: 0, display: 'flex', color: 'var(--text-secondary)' }} aria-hidden="true">
                  <Glyph name={glyphForNotificationType(n.type)} size={20} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 800 }}>{n.title}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{timeAgo(n.createdAt)}</span>
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{n.body}</span>
                  {unreadItem && (
                    <span style={{ display: 'inline-block', marginTop: 6, width: 8, height: 8, borderRadius: '50%', background: 'var(--primary-light)' }} />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
